import { useEffect, useRef } from 'react';

/**
 * Keyboard for a deck screen. The two screens do not have the same keys —
 * the control panel has nothing to make fullscreen and no QR code of its own
 * to show — so the caller passes the map rather than the hook knowing it.
 *
 * Letter keys are matched case-insensitively (`f` and `F` are one binding)
 * and by physical key as well, so the shortcuts still work with the keyboard
 * in Thai — where the F key reports `ด`, not `f`. Typing digits then Enter
 * jumps to that slide number, which is the one piece of behaviour general
 * enough to live here.
 *
 * @param {Object<string, function>} bindings  keyed by KeyboardEvent.key
 * @param {function} [onJump]                  called with a 1-based slide number
 */
export function useDeckKeys(bindings, onJump) {
  // Kept in refs so a re-render never has to re-bind the window listener.
  const mapRef = useRef(bindings);
  const jumpRef = useRef(onJump);
  const bufRef = useRef('');
  mapRef.current = bindings;
  jumpRef.current = onJump;

  useEffect(() => {
    const onKey = (e) => {
      // Never steal a keystroke from something being typed into.
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (e.target && e.target.isContentEditable) return;

      const digit = /^[0-9]$/.test(e.key)
        ? e.key
        : (/^Digit([0-9])$/.exec(e.code || '') || [])[1];

      if (jumpRef.current) {
        if (digit) {
          bufRef.current = (bufRef.current + digit).slice(-3);
          return;
        }
        if (e.key === 'Enter' && bufRef.current) {
          const n = Number(bufRef.current);
          bufRef.current = '';
          jumpRef.current(n);
          return;
        }
      }

      const map = mapRef.current || {};
      const physical = (/^Key([A-Z])$/.exec(e.code || '') || [])[1];
      const handler =
        map[e.key]
        || (e.key.length === 1 ? map[e.key.toLowerCase()] : undefined)
        || (physical ? map[physical.toLowerCase()] : undefined);
      if (!handler) return;
      e.preventDefault();
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
